import { AppIcon, type IconDefinition } from '@/components/ui/app-icon';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { iconMoon, iconSparkles, iconSun } from '@/lib/icons';
import { cn } from '@/lib/utils';
import type { ThemePreference } from '@/store/features/theme.feature';
import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';
import type { KeyboardEvent } from 'react';

const MODES: readonly {
  value: ThemePreference;
  label: string;
  icon: IconDefinition;
}[] = [
  { value: 'system', label: 'System', icon: iconSparkles },
  { value: 'light', label: 'Light', icon: iconSun },
  { value: 'dark', label: 'Dark', icon: iconMoon },
];

const isPreference = (value: string): value is ThemePreference =>
  MODES.some((mode) => mode.value === value);

/**
 * System, Light and Dark as three icon radios, the chosen one raised. Every
 * arrow moves and selects, wrapping, and Home and End select the first and
 * the last, as the preset swatches do.
 */
export function ModeSwitch({
  value,
  onChange,
}: {
  value: ThemePreference;
  onChange: (value: ThemePreference) => void;
}) {
  // Radix moves focus on Home and End but selects only on arrows.
  const onKeyDown = (event: KeyboardEvent) => {
    const target =
      event.key === 'Home'
        ? MODES[0]
        : event.key === 'End'
          ? MODES[MODES.length - 1]
          : null;
    if (target) onChange(target.value);
  };
  return (
    <RadioGroupPrimitive.Root
      aria-label='Mode'
      value={value}
      onValueChange={(next) => {
        if (isPreference(next)) onChange(next);
      }}
      onKeyDown={onKeyDown}
      className='grid grid-cols-3 gap-1 rounded-[10px] bg-muted p-1'
    >
      {MODES.map((mode) => (
        <Tooltip key={mode.value}>
          <TooltipTrigger asChild>
            <RadioGroupPrimitive.Item
              value={mode.value}
              aria-label={mode.label}
              className={cn(
                'flex h-8 items-center justify-center rounded-lg text-muted-foreground transition-colors',
                'hover:text-foreground',
                // aria-checked, not data-state: the tooltip's trigger sets data-state too.
                'aria-checked:bg-background aria-checked:text-primary aria-checked:shadow-xs',
                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden'
              )}
            >
              <AppIcon icon={mode.icon} style={{ fontSize: 14 }} />
            </RadioGroupPrimitive.Item>
          </TooltipTrigger>
          <TooltipContent>{mode.label}</TooltipContent>
        </Tooltip>
      ))}
    </RadioGroupPrimitive.Root>
  );
}
